import { Component, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { AppsService } from "src/app/shared/services/apps.service";
import { PermissionService } from "src/app/services/permission.service";
import moment from "moment";
import { differenceInCalendarDays } from "date-fns";
import { NzMessageService } from "ng-zorro-antd/message";

@Component({
  selector: "app-add-distance",
  templateUrl: "./add-distance.component.html",
  styleUrls: ["./add-distance.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AddDistanceComponent implements OnInit {
  clntDtls: any;
  tntLst: any = [];
  selectedtenant;
  userDtls: any;
  showLdr: boolean = false;
  // True while a distance/working-hours save is in flight — disables submit
  // buttons and prevents duplicate POSTs from rapid clicks.
  isSaving = false;
  vhcleslst: any = [];
  today = new Date();
  yesterday = new Date(new Date().setDate(new Date().getDate() - 1));
  submittedDistances: any[] = [];
  blngPrmtrId;
  dvce_id;
  dateRange: Date[] = [];
  showGrid = false;
  gridData: any[] = [];
  showSubmitButton = false;
  selectedDistances: any[] = [];
  columnDefs: any[] = [];
  editingRowId: number | null = null;
  tempDistance: number | null = null;
  dte: Date | null = null;
  selectedWorkingHours: any[] = [];
  wrkngData: any[] = [];
  wrkngColumnDefs: any[] = [];
  showSubmitWrkngButton = false;
  // Expose Math to template
  Math = Math;
  parseFloat = parseFloat;

  constructor(
    private apiServ: AppsService,
    private message: NzMessageService,
    public perms: PermissionService
  ) {
    this.clntDtls = JSON.parse(localStorage.getItem("wto_clients"));
    this.tntLst = this.clntDtls[0].tnts;
    this.selectedtenant = this.tntLst[0].tnt_id;
    this.userDtls = JSON.parse(localStorage.getItem("wto_w_user"));
  }
  ngOnInit(): void {
    this.getVehicles();
    // Set default dateRange to yesterday
    this.dateRange = [this.yesterday, this.yesterday];
    this.dte = this.yesterday;
  }

  disabledDate = (current: Date): boolean => {
    // Disable today and future dates
    const cur = new Date(current.setHours(0, 0, 0, 0));
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return cur >= today;
  };

  ontntSelect(value: any) {
    if (value === 0) {
      this.selectedtenant = 0;
    }
    for (let i = 0; i < this.tntLst.length; i++) {
      if (this.tntLst[i].tnt_id === value) {
        this.selectedtenant = this.tntLst[i].tnt_id;
      }
    }
    this.getVehicles();
  }

  selectedVehicle: number | null = null;

  getVehicles() {
    const fltrData = [
      {
        selectedtenant: this.selectedtenant,
        selectedvndr: 0,
        selectedctgry: 0,
      },
    ];
    this.showLdr = true;
    this.apiServ
      .post(fltrData, "drivers/vehicles/asgndvehicles")
      .subscribe((res) => {
        if (res["status"] == 200) {
          this.vhcleslst = res["data"];
          this.selectedVehicle = this.vhcleslst[0]?.asrt_id ?? null;
          this.blngPrmtrId = this.vhcleslst[0]?.blng_prmtr_id ?? null;
          this.dvce_id = this.vhcleslst[0]?.dev_imei_nu ?? null;
          this.showLdr = false;
        }
      });
  }
  onVehicleChange(event: any) {
    this.selectedVehicle = event;
    let slctdData = this.vhcleslst.find(
      (v) => v.asrt_id === this.selectedVehicle
    );
    this.blngPrmtrId = slctdData?.blng_prmtr_id ?? null;
    this.dvce_id = slctdData?.dev_imei_nu ?? null;
  }

  // Utility: Check if all rows have valid decimal distance entered
  get allRowsHaveDistance(): boolean {
    return (
      this.gridData.length > 0 &&
      this.gridData.every(
        (row) =>
          row.addedDst !== undefined &&
          row.addedDst !== null &&
          String(row.addedDst).trim() !== ""
      )
    );
  }

  get allRowsHaveWorkingHours(): boolean {
    if (this.wrkngData.length === 0) {
      return false;
    }

    // Separate rows into editable (new data) and existing data rows
    const editableRows = this.wrkngData.filter((row) => row.showTimePickers);
    const existingDataRows = this.wrkngData.filter(
      (row) => row.hasExistingData
    );

    // Check that all editable rows (rows without existing data) are filled
    const allEditableRowsFilled =
      editableRows.length === 0 ||
      editableRows.every((row) => {
        const hasStartTime =
          row.startTime !== null && row.startTime !== undefined;
        const hasAddedTime =
          (row.addedHrs !== null &&
            row.addedHrs !== undefined &&
            row.addedHrs !== "") ||
          (row.addedMins !== null &&
            row.addedMins !== undefined &&
            row.addedMins !== "");
        const hasValidCalculation =
          row.calculatedAddedHrs && parseFloat(row.calculatedAddedHrs) > 0;
        return hasStartTime && hasAddedTime && hasValidCalculation;
      });

    // For rows with existing data, check if user has actually entered a non-zero
    // time change. The +/- sign defaults to '+', so the sign alone must NOT mark a
    // row as "modified" — only a real hours/minutes value (> 0) counts. Rows left
    // at 00:00 are treated as "no change" and don't block submission.
    const modifiedExistingRows = existingDataRows.filter((row) => {
      const h = parseFloat(row.addedHrs) || 0;
      const m = parseFloat(row.addedMins) || 0;
      return h > 0 || m > 0;
    });

    // If user has modified existing data rows, validate them
    const allModifiedRowsValid =
      modifiedExistingRows.length === 0 ||
      modifiedExistingRows.every((row) => {
        const hasSign =
          row.timeSign && (row.timeSign === "+" || row.timeSign === "-");
        const hasHours =
          row.addedHrs !== null &&
          row.addedHrs !== undefined &&
          row.addedHrs !== "";
        const hasMins =
          row.addedMins !== null &&
          row.addedMins !== undefined &&
          row.addedMins !== "";
        const hasValidCalculation =
          row.calculatedAddedHrs && parseFloat(row.calculatedAddedHrs) !== 0;
        return hasSign && (hasHours || hasMins) && hasValidCalculation;
      });

    // Check if at least one row has valid data (either new or modified existing)
    const hasAtLeastOneValidRow =
      editableRows.some((row) => {
        const hasStartTime =
          row.startTime !== null && row.startTime !== undefined;
        const hasAddedTime =
          (row.addedHrs !== null &&
            row.addedHrs !== undefined &&
            row.addedHrs !== "") ||
          (row.addedMins !== null &&
            row.addedMins !== undefined &&
            row.addedMins !== "");
        const hasValidCalculation =
          row.calculatedAddedHrs && parseFloat(row.calculatedAddedHrs) > 0;
        return hasStartTime && hasAddedTime && hasValidCalculation;
      }) ||
      modifiedExistingRows.some((row) => {
        const hasSign =
          row.timeSign && (row.timeSign === "+" || row.timeSign === "-");
        const hasHours =
          row.addedHrs !== null &&
          row.addedHrs !== undefined &&
          row.addedHrs !== "";
        const hasMins =
          row.addedMins !== null &&
          row.addedMins !== undefined &&
          row.addedMins !== "";
        const hasValidCalculation =
          row.calculatedAddedHrs && parseFloat(row.calculatedAddedHrs) !== 0;
        return hasSign && (hasHours || hasMins) && hasValidCalculation;
      });

    // Allow submission if: at least one row is valid AND all editable rows are filled AND all modified existing rows are valid
    return (
      hasAtLeastOneValidRow && allEditableRowsFilled && allModifiedRowsValid
    );
  }

  onGetData() {
    this.gridData = [];
    this.wrkngData = [];
    this.showLdr = true;
    if (this.blngPrmtrId == 1) {
      let postData = {
        asrt_id: this.selectedVehicle,
        from_date: moment(this.dateRange[0]).format("YYYY-MM-DD"),
        to_date: moment(this.dateRange[1]).format("YYYY-MM-DD"),
      };
      this.apiServ
        .post(postData, "drivers/odometer/getdistancebydate")
        .subscribe((res) => {
          if (res["status"] == 200) {
            this.gridData = res["data"];
            this.showLdr = false;
          }
        });
      this.columnDefs = [
        { field: "sno", headerName: "S.No", alignment: "center", width: 80 },
        {
          field: "asrt_nm",
          headerName: "Vehicle",
          alignment: "center",
          width: 180,
        },
        {
          field: "frmt_trvl_dt",
          headerName: "Date",
          alignment: "center",
          width: 140,
        },
        {
          field: "dst",
          headerName: "Distance",
          alignment: "center",
          width: 120,
        },
      ];
      this.showGrid = true;
      this.selectedDistances = [];
    } else {
      let postData = {
        asrt_id: this.selectedVehicle,
        fromdte: moment(this.dateRange[0]).format("YYYY-MM-DD"),
        todte: moment(this.dateRange[1]).format("YYYY-MM-DD"),
      };
      this.apiServ
        .post(postData, "drivers/odometer/getWrkngHrsbydate")
        .subscribe((res) => {
          if (res["status"] == 200) {
            this.wrkngData = res["data"];
            // Calculate total hours for each row and determine if it has existing data
            this.wrkngData = this.wrkngData.map((row) => {
              const hasExistingData =
                row.wrkd_tm && row.wrkd_tm !== "00:00:00" && row.has_data === 1;
              return {
                ...row,
                calculatedHrs: row.wrkd_tm,
                hasExistingData: hasExistingData, // Flag to show if row has existing working hours
                showTimePickers: !hasExistingData, // Show time pickers only if no existing data
                timeSign: "+", // Initialize with '+' (add) by default
              };
            });
            this.showLdr = false;
          }
        });
      this.wrkngColumnDefs = [
        { field: "sno", headerName: "S.No", alignment: "center", width: 80 },
        {
          field: "asrt_nm",
          headerName: "Vehicle",
          alignment: "center",
          width: 180,
        },
        {
          field: "igntn_dt_display",
          headerName: "Ignition Date",
          alignment: "center",
          width: 140,
        },
        {
          field: "wrkd_tm",
          headerName: "Current Working Hours",
          alignment: "center",
          width: 180,
        },
      ];
      this.showGrid = true;
      this.selectedWorkingHours = [];
    }
  }

  onAddDistance(row: any) {
    const rowDate = new Date(row.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (rowDate >= today) {
      alert("Date should be less than today.");
      return;
    }
    this.editingRowId = row.id;
    this.tempDistance = row.distance ? Number(row.distance) : null;
  }

  /**
   * Submit Distance Function
   * API: drivers/odometer/saveDistances
   * Handles vehicle distance submissions with validation
   */
  onSubmitDistances() {
    if (this.isSaving) {
      return;
    }
    if (!this.perms.canWrite()) {
      this.createMessage(
        "error",
        "You do not have permission to save distances."
      );
      return;
    }
    // Only process rows where user entered a distance
    const validRows = this.gridData.filter(
      (row) =>
        row.addedDst !== undefined &&
        row.addedDst !== null &&
        row.addedDst !== ""
    );

    // Check for any invalid distances
    for (const row of validRows) {
      console.log(parseFloat(row.addedDst));
      const distance = parseFloat(row.addedDst);

      // Validate positive values - cannot exceed 30
      if (distance > 0) {
        // if (distance > 30) {
        //   this.createMessage('error', `Distance cannot exceed 30 kms (Row ${row.sno || ''})`);
        // return;
        // }
      }
      // Validate negative values - check if it's a valid negative number
      else if (distance < 0) {
        if (isNaN(distance)) {
          this.createMessage(
            "error",
            `Invalid distance value (Row ${row.sno || ""})`
          );
          return;
        }
        // Negative values are allowed - continue processing
      }
      // If distance is exactly 0, it's also valid
    }

    // Prepare array of only valid entered distances
    this.selectedDistances = validRows.map((row) => ({
      dst: parseFloat(row.addedDst),
      sbmtd_in: 1,
      asrt_id: row.asrt_id,
      dte: moment(row.trvl_dt).format("YYYY-MM-DD"),
      tnt_id: this.selectedtenant,
    }));

    if (this.selectedDistances.length === 0) {
      this.createMessage(
        "warning",
        "Please enter at least one distance before submitting."
      );
      return;
    }

    this.showLdr = true;
    this.isSaving = true;
    // Call Distance API endpoint
    this.apiServ
      .post(this.selectedDistances, "drivers/odometer/saveDistances")
      .subscribe(
        (res) => {
          this.showLdr = false;
          this.isSaving = false;
          if (res["status"] == 200) {
            this.createMessage("success", "Distances submitted successfully.");
            // Reset distance-related data
            this.gridData = [];
            this.showGrid = false;
            this.selectedVehicle = null;
            this.dateRange = [];
            this.showSubmitButton = false;
            this.selectedDistances = [];
          }
        },
        (error) => {
          this.showLdr = false;
          this.isSaving = false;
          this.createMessage(
            "error",
            "Failed to save distances. Please try again."
          );
          console.error("Distance submission error:", error);
        }
      );
  }

  onCancelDistance() {
    this.editingRowId = null;
    this.tempDistance = null;
  }

  onCellClick(e: any) {
    if (e.column.caption === "Add Distance") {
      this.showSubmitButton = true;
      this.selectedDistances.push(e.row.data);
    }
  }
  createMessage(type: string, msg: string): void {
    this.message.create(type, msg);
  }
  onWrkngCellClick(e: any) {
    if (e.column.caption === "Start Time" || e.column.caption === "Add Time") {
      this.showSubmitWrkngButton = true;
    }
  }

  // Calculate working hours when user enters start time + hours/mins (for rows without existing data)
  onNewRowTimeChange(row: any) {
    const hours = parseFloat(row.addedHrs) || 0;
    const minutes = parseFloat(row.addedMins) || 0;

    // Validate minutes range
    if (minutes < 0 || minutes > 59) {
      this.createMessage("error", "Minutes must be between 0 and 59.");
      row.addedMins = null;
      row.calculatedAddedHrs = "0.00";
      row.futureHrs = null;
      row.startTimeDisplay = null;
      return;
    }

    if (hours < 0) {
      this.createMessage("error", "Hours must be 0 or positive.");
      row.addedHrs = null;
      row.calculatedAddedHrs = "0.00";
      row.futureHrs = null;
      row.startTimeDisplay = null;
      return;
    }

    const totalHours = hours + minutes / 60;

    // Set start time display string
    if (row.startTime) {
      row.startTimeDisplay = moment(row.startTime).format("HH:mm");
    } else {
      row.startTimeDisplay = null;
    }

    // Validate: Maximum 12 hours can be added
    if (totalHours > 12) {
      this.createMessage("error", "Maximum 12 hours can be added.");
      row.addedHrs = null;
      row.addedMins = null;
      row.calculatedAddedHrs = "0.00";
      row.futureHrs = null;
      row.futureHrsDisplay = null;
      row.calculatedFromTime = null;
      row.calculatedToTime = null;
      row.endTimeDisplay = null;
      return;
    }

    if (totalHours > 0 && row.startTime) {
      row.calculatedAddedHrs = totalHours.toFixed(2);
      row.futureHrs = totalHours.toFixed(2);
      row.futureHrsDisplay = this.convertHoursToTimeFormat(totalHours);

      // Calculate end time from start time + added hours for API
      const startMoment = moment(row.startTime);
      const endMoment = startMoment
        .clone()
        .add(hours, "hours")
        .add(minutes, "minutes");
      row.calculatedFromTime = startMoment.format("HH:mm:ss");
      row.calculatedToTime = endMoment.format("HH:mm:ss");
      row.endTimeDisplay = endMoment.format("HH:mm");

      this.showSubmitWrkngButton = true;
    } else {
      row.calculatedAddedHrs = totalHours > 0 ? totalHours.toFixed(2) : "0.00";
      row.futureHrs = totalHours > 0 ? totalHours.toFixed(2) : null;
      row.futureHrsDisplay =
        totalHours > 0 ? this.convertHoursToTimeFormat(totalHours) : null;
      row.calculatedFromTime = null;
      row.calculatedToTime = null;
      row.endTimeDisplay = null;
    }
  }

  // Handle adding/subtracting time (hours and minutes) to existing working hours data
  onAddTimeChange(row: any) {
    // Initialize timeSign to '+' if not set
    if (!row.timeSign) {
      row.timeSign = "+";
    }

    const hours = parseFloat(row.addedHrs) || 0;
    const minutes = parseFloat(row.addedMins) || 0;
    const sign = row.timeSign || "+";

    // Validate hours and minutes are non-negative (sign is handled separately)
    if (hours < 0) {
      this.createMessage(
        "error",
        "Hours must be 0 or positive. Use +/- selector for sign."
      );
      row.addedHrs = null;
      row.calculatedAddedHrs = "0.00";
      return;
    }

    // Validate minutes range
    if (minutes < 0 || minutes > 59) {
      this.createMessage("error", "Minutes must be between 0 and 59.");
      row.addedMins = null;
      row.calculatedAddedHrs = "0.00";
      return;
    }

    // Calculate total hours (hours + minutes/60) and apply sign
    let totalHours = hours + minutes / 60;

    // Validate: Maximum 12 hours can be added or subtracted
    if (totalHours > 12) {
      this.createMessage(
        "error",
        "Maximum 12 hours can be added or subtracted."
      );
      row.addedHrs = null;
      row.addedMins = null;
      row.calculatedAddedHrs = "0.00";
      row.totalResultHrs = "0.00";
      row.futureHrs = null;
      row.futureHrsDisplay = null;
      return;
    }

    if (sign === "-") {
      totalHours = -totalHours; // Make it negative for subtraction
    }

    if (totalHours !== 0) {
      const currentHrs = this.convertTimeToHours(row.wrkd_tm);

      // Validate: When subtracting, ensure it's not more than current working hours
      if (totalHours < 0 && Math.abs(totalHours) > currentHrs) {
        this.createMessage(
          "error",
          `Cannot subtract more than current working hours. Current: ${this.convertHoursToTimeFormat(
            currentHrs
          )}`
        );
        row.addedHrs = null;
        row.addedMins = null;
        row.calculatedAddedHrs = "0.00";
        row.totalResultHrs = "0.00";
        row.futureHrs = null;
        row.futureHrsDisplay = null;
        return;
      }

      row.calculatedAddedHrs = totalHours.toFixed(2);
      // Calculate and store the total result (current + added/subtracted)
      const futureTotal = currentHrs + totalHours;
      row.totalResultHrs = futureTotal.toFixed(2);
      row.futureHrs = futureTotal.toFixed(2);
      row.futureHrsDisplay = this.convertHoursToTimeFormat(futureTotal);
      row.addedHrsDisplay =
        (totalHours > 0 ? "+" : "") +
        this.convertHoursToTimeFormat(Math.abs(totalHours));
      this.showSubmitWrkngButton = true;
    } else {
      row.calculatedAddedHrs = "0.00";
      row.totalResultHrs = this.convertTimeToHours(row.wrkd_tm).toFixed(2);
      row.futureHrs = null;
      row.futureHrsDisplay = null;
      row.addedHrsDisplay = null;
    }
  }

  /**
   * Submit Working Hours Function
   * API: drivers/odometer/saveWorkingHours
   * Handles vehicle working hours submissions with time selection and validation
   * Supports both INSERT (new data) and UPDATE (add to existing) operations
   */
  onSubmitWorkingHours() {
    if (this.isSaving) {
      return;
    }
    if (!this.perms.canWrite()) {
      this.createMessage(
        "error",
        "You do not have permission to save working hours."
      );
      return;
    }
    // Filter valid rows - either with start time + add time (new data) or added time (update existing)
    const validRows = this.wrkngData.filter((row) => {
      // For rows without existing data - need start time + added hours/mins
      if (row.showTimePickers) {
        return (
          row.startTime &&
          row.calculatedAddedHrs &&
          parseFloat(row.calculatedAddedHrs) > 0
        );
      }
      // For rows with existing data - need added hours or minutes (positive or negative)
      else if (row.hasExistingData) {
        return (
          row.calculatedAddedHrs && parseFloat(row.calculatedAddedHrs) !== 0
        );
      }
      return false;
    });

    // Validate the working hours input
    for (const row of validRows) {
      const hours = parseFloat(row.calculatedAddedHrs); // Already calculated from hours + minutes

      if (isNaN(hours) || hours === 0) {
        this.createMessage(
          "error",
          `Invalid input (Row ${row.sno || ""}). Please enter valid hours.`
        );
        return;
      }

      // Validate: Maximum 12 hours
      if (Math.abs(hours) > 12) {
        this.createMessage(
          "error",
          `Maximum 12 hours can be added or subtracted (Row ${row.sno || ""}).`
        );
        return;
      }

      // Get current hours for validation
      const currentHrs = this.convertTimeToHours(row.wrkd_tm);

      if (hours > 0) {
        // Validate that total hours (current + added) don't exceed 24 hours per day
        const totalHrs = currentHrs + hours;
        if (totalHrs > 24) {
          this.createMessage(
            "error",
            `Total working hours cannot exceed 24:00 per day (Row ${
              row.sno || ""
            }). Current: ${this.convertHoursToTimeFormat(
              currentHrs
            )}, Adding: ${this.convertHoursToTimeFormat(hours)}`
          );
          return;
        }
      } else if (hours < 0) {
        // Validate that we're not subtracting more than available hours
        if (Math.abs(hours) > currentHrs) {
          this.createMessage(
            "error",
            `Cannot subtract more than current working hours (Row ${
              row.sno || ""
            }). Current: ${this.convertHoursToTimeFormat(
              currentHrs
            )}, Subtracting: ${this.convertHoursToTimeFormat(Math.abs(hours))}`
          );
          return;
        }
      }
    }

    if (validRows.length === 0) {
      this.createMessage(
        "warning",
        "Please enter working hours data for at least one row before submitting."
      );
      return;
    }

    // Prepare array of valid working hours with time details and operation indicator
    this.selectedWorkingHours = validRows.map((row) => {
      const baseData = {
        hrs: parseFloat(row.calculatedAddedHrs), // Combined hours + minutes value
        asrt_id: row.asrt_id,
        dte: moment(row.igntn_dt).format("YYYY-MM-DD"), // Raw date in YYYY-MM-DD format from backend
        tnt_id: this.selectedtenant,
        is_update: row.hasExistingData, // Indicator: true = UPDATE existing, false = INSERT new
        has_existing_data: row.hasExistingData,
        dvce_id: this.dvce_id,
      };

      // Add time fields only for INSERT operations (new data)
      if (
        !row.hasExistingData &&
        row.calculatedFromTime &&
        row.calculatedToTime
      ) {
        return {
          ...baseData,
          from_time: row.calculatedFromTime,
          to_time: row.calculatedToTime,
        };
      }

      return baseData;
    });

    this.showLdr = true;
    this.isSaving = true;
    // Call Working Hours API endpoint
    this.apiServ
      .post(this.selectedWorkingHours, "drivers/odometer/saveWorkingHours")
      .subscribe(
        (res) => {
          this.isSaving = false;
          if (res["status"] == 200) {
            this.createMessage(
              "success",
              "Working hours submitted successfully. Updating displayed data..."
            );
            this.showSubmitWrkngButton = false;
            this.selectedWorkingHours = [];
            // onGetData() resets showLdr after the refresh completes
            this.onGetData();
          } else {
            this.showLdr = false;
          }
        },
        (error) => {
          this.showLdr = false;
          this.isSaving = false;
          this.createMessage(
            "error",
            "Failed to save working hours. Please try again."
          );
          console.error("Working hours submission error:", error);
        }
      );
  }

  // Helper function to convert time string (HH:MM:SS) to hours
  convertTimeToHours(timeStr: string): number {
    if (!timeStr) return 0;
    const parts = timeStr.split(":");
    const hours = parseInt(parts[0]) || 0;
    const minutes = parseInt(parts[1]) || 0;
    const seconds = parseInt(parts[2]) || 0;
    return hours + minutes / 60 + seconds / 3600;
  }

  // Helper function to convert decimal hours to HH:mm format
  convertHoursToTimeFormat(decimalHours: number): string {
    const h = Math.floor(decimalHours);
    const m = Math.round((decimalHours - h) * 60);
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  }
}
